import type { JSX } from 'react';
import { ProcessAccordion } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { ServiceCode } from '@common/enums';
import type { PatientData } from '@common/interfaces/serviceContext.types';
import { assertIsNonNullable } from '@common/utils/nonNullableTypeUtils';
import { getServiceContext } from '@frontend/services/api/patientsApiService';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { useDefinedContext } from '@frontend/utils/hooks/useDefinedContext';
import { usePatientEpisodeCategoryContext } from '@frontend/utils/hooks/usePatientEpisodeCategoryContext';
import { PatientEpisodeCategoryCarePathContext } from '../patientEpisodeCategoryCarePathContext';
import { ProcessActivityContextProvider } from './processActivity/ProcessActivityContextProvider';
import { ProcessActivityEdit } from './processActivity/processActivityEdit/ProcessActivityEdit';

export function ProcessStart(): JSX.Element | null {
    const abortSignal = useAbortSignal();
    const { patient } = usePatientEpisodeCategoryContext();
    const { activeStartingPoint } = useDefinedContext(PatientEpisodeCategoryCarePathContext);
    assertIsNonNullable(activeStartingPoint);
    // TODO: CS-23169 Remove this when ServiceWorkflowStepActivities table is deleted and every starting point has exactly one activity
    const activity = activeStartingPoint.activities[0];
    const [isLoading, setIsLoading] = useState(true);
    const [patientData, setPatientData] = useState<PatientData | undefined>();

    useEffect(() => {
        loadPatientServiceContext(patient.id, activeStartingPoint.service.code);
    }, [activeStartingPoint, patient.id]);

    if (isLoading) {
        return null;
    }

    return (
        <ProcessAccordion>
            <ProcessActivityContextProvider
                customControlCode={activity.customControlCode}
                customControlVersion={activity.customControlVersion}
                patientData={patientData}
                serviceActivity={{ ...activity, isPrintEnabled: false }}
                serviceCode={activeStartingPoint.service.code}
                serviceWorkflowStep={activeStartingPoint.serviceWorkflowStep}
            >
                <ProcessActivityEdit />
            </ProcessActivityContextProvider>
        </ProcessAccordion>
    );

    async function loadPatientServiceContext(patientId: string, serviceCode: ServiceCode): Promise<void> {
        setIsLoading(true);
        const patientServiceContext = await getServiceContext(patientId, serviceCode, abortSignal);
        setPatientData(patientServiceContext?.patientData);
        setIsLoading(false);
    }
}
